import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SortByList from '../SortByList';
import NoHiddenJobs from './NoHiddenJobs';
import JobBoardPagination from '../JobBoardPagination';
import JobCard from '../JobCard';
import SkeletonJobCard from '../SkeletonJobCard';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import {
  getMyHiddenJobPostsList,
  getJobPostDetail,
  getJobPostOptions,
  hideJobPostForUser,
  unhideJobPostForUser,
} from '../../../api/jobboard/jobboard.api';
import {
  buildJobOptionMeta,
  mapJobPostDetailToCardModel,
  mapJobPostListToCardModel,
  Chip,
  FilterBottomSheet,
  Toast,
  useIsMobile,
  useT,
} from '@repo/common';

const SORT_OPTIONS = ['By Date', 'By Deadline'];

const HiddenJobs = () => {
  const t = useT();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState('By Date');
  const [loading, setLoading] = useState(true);
  const [restoreToastKey, setRestoreToastKey] = useState(0);
  const PAGE_SIZE = 12;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchHiddenJobs = async () => {
      try {
        setLoading(true);

        const [listData, optionGroups] = await Promise.all([
          getMyHiddenJobPostsList(),
          getJobPostOptions(),
        ]);

        const optionMeta = buildJobOptionMeta(optionGroups || []);

        const enriched = await Promise.all(
          (listData || []).map(async (post) => {
            try {
              const detail = await getJobPostDetail(post.id);
              return mapJobPostDetailToCardModel(detail, optionMeta);
            } catch (error) {
              console.error(`Hidden detail fail (${post.id})`, error);
              return mapJobPostListToCardModel(post);
            }
          }),
        );

        setJobs(enriched);
      } catch (err) {
        console.error(err);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHiddenJobs();
  }, []);

  const handleToggleHiddenForUser = async (jobId, nextHidden) => {
    if (!jobId) return;

    try {
      if (nextHidden) {
        await hideJobPostForUser(jobId);

        setJobs((prev) =>
          prev.map((job) =>
            job.id === jobId ? { ...job, isHiddenByUser: true } : job,
          ),
        );
      } else {
        await unhideJobPostForUser(jobId);

        setJobs((prev) => prev.filter((job) => job.id !== jobId));
        /*
         * 카드가 목록에서 사라지기만 하면 성공했는지 알 수 없어 토스트로 알린다.
         * 연달아 복구하면 이미 떠 있는 토스트의 3초 타이머가 그대로라 금방 사라지므로,
         * 값을 올려 key 로 쓰고 다시 마운트시킨다.
         */
        setRestoreToastKey((prev) => prev + 1);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const totalPages = Math.max(1, Math.ceil(jobs.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedJobs = jobs.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  return (
    <div className='flex flex-1 flex-col items-center max-w-[870px] w-full p-4 pb-[60px] tabletH:p-0 tabletH:pb-10'>
      <div className='flex flex-col tabletH:flex-row items-start tabletH:items-center justify-between w-full px-1 pb-4 tabletH:px-0 tabletH:pb-[30px]'>
        {!isMobile && (
          <div className='typo-h5 text-text-primary'>Hidden Jobs</div>
        )}

        <div className='relative'>
          <Chip
            icon={isSortOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            iconPosition={isMobile ? 'before' : 'after'}
            disableTypography={true}
            bgclass='bg-transparent gap-1 tabletH:gap-2 typo-b13Strong tabletH:typo-b14 p-1 pb-2 tabletH:px-4 tabletH:py-3'
            textclass='text-grayscale-gray07'
            iconclass='text-icon-primary'
            borderclass='ring-0'
            onClick={(e) => {
              e.stopPropagation();
              setIsSortOpen((prev) => !prev);
            }}
          >
            {sortBy}
          </Chip>

          {isSortOpen &&
            (isMobile ? (
              <FilterBottomSheet
                options={SORT_OPTIONS}
                selected={sortBy}
                onSelect={(option) => {
                  setSortBy(option);
                  setIsSortOpen(false);
                }}
                onClose={() => setIsSortOpen(false)}
              />
            ) : (
              <div className='absolute top-[42px] right-0 z-10'>
                <SortByList
                  selected={sortBy}
                  onSelect={(option) => {
                    setSortBy(option);
                    setIsSortOpen(false);
                  }}
                />
              </div>
            ))}
        </div>
      </div>

      {loading ? (
        <div className='flex flex-col gap-6 w-full'>
          {Array.from({ length: 12 }).map((_, idx) => (
            <SkeletonJobCard key={idx} />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className='w-full flex flex-1 flex-col gap-4 tabletH:flex-none tabletH:gap-0'>
          <NoHiddenJobs />
          <div className='mt-auto pt-6 tabletH:mt-0 tabletH:pt-0'>
            <JobBoardPagination current={1} totalPages={1} />
          </div>
        </div>
      ) : (
        <div className='flex flex-1 flex-col gap-4 tabletH:flex-none tabletH:gap-0 w-full'>
          <div className='flex flex-col gap-4 tabletH:gap-6 w-full pb-4 tabletH:pb-10'>
            {pagedJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                isAllHiddenJobPage
                onToggleHidden={handleToggleHiddenForUser}
                $isMobile={isMobile}
              />
            ))}
          </div>
          <div className='mt-auto pt-6 tabletH:mt-0 tabletH:pt-0'>
            <JobBoardPagination
              current={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      {restoreToastKey > 0 && (
        <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
          <Toast
            key={restoreToastKey}
            type='success'
            title={t('Job restored', '공고를 복구했습니다')}
            buttonLabel={t('View Jobs', '공고 보기')}
            onButtonClick={() => navigate('/jobs')}
            onClose={() => setRestoreToastKey(0)}
          />
        </div>
      )}
    </div>
  );
};

export default HiddenJobs;
